import type { DiscountReport } from '@salonomy/types';
import { Card, Pill, Stat } from '@salonomy/ui';
import { useDiscountReport, useDiscounts } from '../../data/discounts.js';
import { money, moneyWhole } from '../../lib/format.js';
import { DISCOUNT_REASON_LABELS } from '../till/reasonLabels.js';
import { DateRange, useDateRange } from './shared.js';

/**
 * WS9 §3 — the discount audit made reportable: WHERE margin is going, by reason code (useDiscountReport),
 * plus the recent applied-discount audit list (useDiscounts). Both are server-computed; no money is summed
 * in JS — the report's totals and the audit rows are the server's figures, shown as-is.
 *
 * The audit/report endpoints filter on an ISO-datetime window, so the date-range control's YYYY-MM-DD
 * bounds are widened to a [start-of-from, end-of-to] day window before being passed (a date selection, not
 * a money calculation). Manager-gated: the report endpoint is manager-only on the API.
 */

/** YYYY-MM-DD → the inclusive ISO-datetime window bounds the discount endpoints expect. */
function dayStartIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}
function dayEndIso(date: string): string {
  return `${date}T23:59:59.999Z`;
}

export function DiscountsReportPanel({ isFloorManager }: { isFloorManager: boolean }) {
  if (!isFloorManager) {
    return (
      <Card>
        <div className="section-title">Discounts</div>
        <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
          The discount report and audit trail are available to salon owners and managers.
        </div>
      </Card>
    );
  }
  return <DiscountsReportBody />;
}

function DiscountsReportBody() {
  const control = useDateRange();
  const from = dayStartIso(control.from);
  const to = dayEndIso(control.to);

  const { data: report, isLoading, isError } = useDiscountReport({ from, to });
  const { data: audit } = useDiscounts({ from, to }, 0);

  return (
    <Card>
      <div className="section-title">Discounts — where margin is going</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 12 }}>
        Every checkout discount carries a reason — no silent overrides. Totals are the server’s,
        broken out by reason so you can see where margin is going.
      </div>

      <div style={{ marginBottom: 16 }}>
        <DateRange control={control} />
      </div>

      {isError ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Couldn’t load the discount report for this period.
        </div>
      ) : isLoading || !report ? (
        <div className="muted" style={{ fontSize: 13 }}>
          Loading discount report…
        </div>
      ) : (
        <ReportBody report={report} />
      )}

      {audit && audit.data.length > 0 ? (
        <div style={{ marginTop: 24 }}>
          <div className="section-title" style={{ fontSize: 15 }}>
            Recent discounts
          </div>
          <div className="settings-list" style={{ marginTop: 8 }}>
            {audit.data.slice(0, 20).map((d) => (
              <div key={d.id} className="settings-row">
                <div>
                  <div style={{ fontWeight: 600 }}>{DISCOUNT_REASON_LABELS[d.reasonCode]}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>
                    {new Date(d.createdAt).toLocaleString('en-GB')}
                    {d.reasonNote ? ` · ${d.reasonNote}` : ''}
                  </div>
                </div>
                <Stat label="Off" value={money(d.discountPence)} />
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </Card>
  );
}

function ReportBody({ report }: { report: DiscountReport }) {
  if (report.discountCount === 0) {
    return (
      <div className="muted" style={{ fontSize: 13 }}>
        No discounts applied in this period.
      </div>
    );
  }
  return (
    <>
      <div
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'baseline',
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <span className="slm-stat__label">Total discounted · this period</span>
          <div
            style={{
              fontFamily: 'var(--slm-font-display)',
              fontSize: 36,
              lineHeight: 1.05,
              marginTop: 4,
            }}
          >
            {moneyWhole(report.totalDiscountPence)}
          </div>
        </div>
        <Pill tone="neutral">
          {report.discountCount} discount{report.discountCount === 1 ? '' : 's'}
        </Pill>
      </div>

      <div className="settings-list">
        {report.byReason.map((r) => (
          <div key={r.reasonCode} className="settings-row">
            <div>
              <div style={{ fontWeight: 600 }}>{DISCOUNT_REASON_LABELS[r.reasonCode]}</div>
              <div className="muted" style={{ fontSize: 12.5 }}>
                {r.count} discount{r.count === 1 ? '' : 's'}
              </div>
            </div>
            <Stat label="Total off" value={money(r.totalDiscountPence)} />
          </div>
        ))}
      </div>
    </>
  );
}
